/**
 * Tipo de Seguro — código vindo do BFF (`typeOfInsurance` / `policyType`),
 * enviado como string numérica. Use sempre estes valores e o
 * `getInsuranceTypeLabel` para exibir o rótulo, nunca um mapa local.
 */
export enum InsuranceType {
  /** Seguro novo */
  New = '1',
  /** Renovação */
  Renewal = '2',
  /** Renovação externa (congênere) */
  ExternalRenewal = '3',
}

/**
 * Rótulos canônicos por código de tipo de seguro, exibidos nos resumos de
 * cotação/proposta/apólice. Telas com copy próprio aplicam override local
 * em cima deste map (padrão igual ao `PORTAL_STATUS_LABELS`).
 */
export const INSURANCE_TYPE_LABELS: Record<string, string> = {
  [InsuranceType.New]: 'Novo',
  [InsuranceType.Renewal]: 'Renovação',
  [InsuranceType.ExternalRenewal]: 'Renovação Externa',
};

/**
 * Resolve o rótulo de exibição para um código de tipo de seguro.
 * @param code código vindo do BFF (string ou número)
 * @param fallback valor retornado quando o código é vazio/desconhecido (default `'—'`)
 */
export function getInsuranceTypeLabel(
  code?: string | number | null,
  fallback = '—'
): string {
  const key = String(code ?? '').trim();
  if (!key) return fallback;
  return INSURANCE_TYPE_LABELS[key] ?? fallback;
}

/**
 * Resolve o rótulo de tipo de seguro de uma cotação/proposta/apólice.
 *
 * A escolha do usuário (Novo/Renovação/Renovação Externa) chega do BFF em
 * `typeOfInsurance` (InsureMO `TipoSeguro`). Não use `policyType`: esse é o
 * `PolicyType` do InsureMO, o tipo estrutural da apólice, que permanece `'1'`
 * mesmo em renovação e faz a tela exibir "Novo" sempre.
 *
 * Registros antigos sem `TipoSeguro` caem no legado `isTakeUpPolicy` — mesma
 * regra de fallback que o BFF usa nos PDFs.
 */
export function resolveInsuranceTypeLabel(
  policy?: {
    typeOfInsurance?: string | number | null;
    isTakeUpPolicy?: string | null;
  } | null,
  fallback = '—'
): string {
  const label = getInsuranceTypeLabel(policy?.typeOfInsurance, '');
  if (label) return label;

  if (policy?.isTakeUpPolicy === 'Y')
    return INSURANCE_TYPE_LABELS[InsuranceType.Renewal];
  if (policy?.isTakeUpPolicy === 'N')
    return INSURANCE_TYPE_LABELS[InsuranceType.New];

  return fallback;
}
